import { useTranslation } from 'react-i18next';

import { Helmet } from '@dr.pogodin/react-helmet';
import classNames from 'classnames';

import {
  PageLayout,
  PageTitle,
  PageTitleProps,
  Spinner,
  UnavailableMessage,
} from '@anyx/shared-ui';
import { useDocumentHead, useEnvironmentStore } from '@anyx/shared-utils';


export interface CrmPageBuilderRootProps {
  children: React.ReactNode;
  responsive?: boolean;
  width?: 'fluid' | 'fit';
  center?: boolean;
  className?: string;
}

const Root = ({
  children,
  responsive = true,
  center = false,
  className,
}: CrmPageBuilderRootProps) => {
  return (
    <PageLayout fullPage withFooter>
      <div
        className={classNames(
          'relative h-full',
          {
            'lg:px-6 lg:pt-4': responsive,
            'px-6 pt-4': !responsive,
            'mx-auto': center,
          },
          className
        )}
      >
        {children}
      </div>
    </PageLayout>
  );
};

export interface CrmPageBuilderDocumentHeadProps {
  pageTitle?: string;
}

const DocumentHead = ({ pageTitle }: CrmPageBuilderDocumentHeadProps) => {
  const { t } = useTranslation('shared');
  const appName = useEnvironmentStore((s) => s.appName);
  const crmName = t('shared.entity.crm', { count: 1, ns: 'shared' });

  const { documentTitle } = useDocumentHead({
    pageTitle,
    appName: `${crmName} | ${appName}`,
  });

  return (
    <Helmet>
      <title>{documentTitle}</title>
    </Helmet>
  );
};

export type CrmPageBuilderTitleProps = PageTitleProps;

const Title = (props: CrmPageBuilderTitleProps) => {
  return <PageTitle {...props} />;
};

export interface CrmPageBuilderContentProps {
  loading?: boolean;
  error?: boolean;
  children: React.ReactNode;
}

const Content = ({ loading, error, children }: CrmPageBuilderContentProps) => {
  if (loading) {
    return (
      <div className="grid h-full w-full grow place-items-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <UnavailableMessage />;
  }

  return <>{children}</>;
};

export type CrmPageBuilderTableContentProps = CrmPageBuilderContentProps;

const TableContent = ({ children, ...rest }: CrmPageBuilderTableContentProps) => {
  return (
    <Content {...rest}>
      <div className="flex flex-col gap-y-4 [&>nav]:px-4 [&>nav]:py-0 lg:[&>nav]:px-0">
        {children}
      </div>
    </Content>
  );
};

export const CrmPageBuilder = {
  Root,
  DocumentHead,
  Title,
  Content,
  TableContent,
};
